import { memo, useCallback, useMemo, useState } from 'react';

import { Modal, Button, Rate, Radio, Input, Typography } from 'antd';

import { Status } from '../../../../../fsm/jobs';

import styles from './index.module.css';

const { TextArea } = Input;
const { Text } = Typography;

export interface DebriefPayload {
  /**
   * The hiring manager's own rating, 1-5, and omitted when they did not rate.
   *
   * Not zero. `addOrUpdateFeedback` writes the rating only when it is non-null,
   * and its tests pin two things a zero breaks: an unrated record must stay
   * distinguishable from one rated zero, and a comment-only edit must not erase
   * a rating already stored. Zero passes the non-null guard, so sending it would
   * record "rated zero" now and overwrite a stored 4.5 the next time someone
   * reopens the box to fix a typo.
   */
  rating?: number;
  /**
   * Structured reason, so the corpus has something comparable across debriefs.
   *
   * WARNING FOR WHOEVER WIRES THE REQUEST: the `Feedback` schema in
   * avrio-backend's openapi.yaml has exactly two properties, `text` and
   * `rating`. There is no `reason`. Sending one as its own field drops it
   * silently -- the generated DTO has no property for it and
   * FAIL_ON_UNKNOWN_PROPERTIES is false, which is precisely how the rating was
   * lost before #36.
   *
   * So do not pass `reason` up as a sibling of `text` and assume it lands.
   * Either fold it into `text` (first line, free text after a blank line --
   * preserves it in a field that exists, at the cost of machine-comparability)
   * or get a `reason` property added to the schema first. The second is the
   * better data; the first is the one that cannot silently lose anything.
   */
  reason: string;
  /** Optional free text. Persisted today as hmFeedbackComments. */
  text: string;
}

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  /** The decision already recorded. Shown back so the modal reads as a follow-up. */
  decision?: Status;
  candidateName?: string;
  onClose: () => void;
  onSubmit: (payload: DebriefPayload) => void;
}

/**
 * The debrief, asked at the moment of the stage change.
 *
 * RIO-7.2 makes this the ground-truth label: Rio's score is a prediction and
 * the hiring manager's rating is the outcome it predicts, so the two are
 * compared (RIO-7.5). RIO-7.3 gives it two minutes and requires it here rather
 * than in a later email or a dashboard someone has to visit. RIO-7.10 forbids
 * making it a gate.
 *
 * So: the decision is already saved before this opens. Every control is
 * optional, Escape and the mask close it, and dismissing changes nothing about
 * the candidate's stage. Three inputs, one screen, no second step.
 */
const DebriefModal = ({
  isOpen,
  isLoading,
  decision,
  candidateName,
  onClose,
  onSubmit,
}: Props) => {
  const [rating, setRating] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [text, setText] = useState<string>('');

  // Reasons differ by direction. Asking why someone was hired from a list of
  // rejection reasons is the sort of thing that makes people close the box.
  const reasons = useMemo(() => {
    if (decision === Status.HIRED) {
      return [
        'Stronger than the score suggested',
        'Score matched what we saw',
        'Hired despite reservations',
        'Other',
      ];
    }
    return [
      'Weaker than the score suggested',
      'Score matched what we saw',
      'Strong, but not for this role',
      'Withdrew or unavailable',
      'Other',
    ];
  }, [decision]);

  const reset = useCallback(() => {
    setRating(0);
    setReason('');
    setText('');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(() => {
    // Local state stays 0 because antd's Rate takes a number. The payload omits
    // the field instead, so "did not rate" reaches the API as absent.
    onSubmit({ rating: rating > 0 ? rating : undefined, reason, text });
    reset();
    onClose();
  }, [rating, reason, text, onSubmit, reset, onClose]);

  const heading =
    decision === Status.HIRED
      ? 'Moved to hired'
      : decision === Status.REJECTED
        ? 'Marked as rejected'
        : 'Stage updated';

  return (
    <Modal
      title="How did we do?"
      open={isOpen}
      confirmLoading={isLoading}
      onCancel={handleClose}
      maskClosable
      keyboard
      footer={
        <>
          <Button onClick={handleClose}>Skip</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!rating && !reason && !text.trim()}
          >
            Send debrief
          </Button>
        </>
      }
    >
      <div className={styles.content}>
        <Text type="secondary" className={styles.saved}>
          {heading}
          {candidateName ? ` for ${candidateName}` : ''}. Saved already &mdash;
          this is optional and takes about a minute.
        </Text>

        <div className={styles.field}>
          <Text strong>Your own rating of this candidate</Text>
          <Text type="secondary" className={styles.hint}>
            Rate the candidate, not Rio. The comparison is what tells us whether
            the Fit Score predicts anything.
          </Text>
          <Rate value={rating} onChange={setRating} />
        </div>

        <div className={styles.field}>
          <Text strong>Closest reason</Text>
          <Radio.Group
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={styles.reasons}
          >
            {reasons.map((option) => (
              <Radio key={option} value={option} className={styles.reason}>
                {option}
              </Radio>
            ))}
          </Radio.Group>
        </div>

        <div className={styles.field}>
          <Text strong>Anything else</Text>
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            showCount
            rows={3}
            maxLength={500}
            placeholder="Optional"
          />
        </div>
      </div>
    </Modal>
  );
};

export default memo(DebriefModal);
